import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Checkbox, Dropdown, Button, Icon, Message } from 'semantic-ui-react'
import { updateFormData } from '../../../redux/slices/listingSlice'
import PriceInputUI from '../../input/PriceInputUI'
import RentDurationInputUI from '../../input/RentDurationInputUI'
import {
  selectCurrency
} from '../../../redux/slices/currencySlice'
import { useGetCurrentUserQuery, useGetAddressesQuery } from '../../../redux/apis'
import { determineExpectedCurrency } from '../../../utils/determineOriginCurrency'

// Define period options
const PERIOD_OPTIONS = [
  { key: 'day', text: 'Day(s)', value: 'day' },
  { key: 'week', text: 'Week(s)', value: 'week' },
  { key: 'month', text: 'Month(s)', value: 'month' },
  { key: 'hour', text: 'Hour(s)', value: 'hour' } // Add hour option for local services
]

const PricingSection = ({ isVirtualFashion = false, isLocalService = false }) => {
  const dispatch = useDispatch()
  const formData = useSelector(state => state.listing.form.formData)
  const fieldErrors = useSelector(state => state.listing.form.fieldErrors)
  const defaultCurrency = useSelector(selectCurrency)
  
  const { data: currentUser, isLoading: isLoadingUser } = useGetCurrentUserQuery()
  const { data: addresses = [], isLoading: isLoadingAddresses } = useGetAddressesQuery()

  const [priceValue, setPriceValue] = useState('')
  const [depositValue, setDepositValue] = useState('')
  const [originDeterminedCurrency, setOriginDeterminedCurrency] = useState(null)

  // Central useEffect to determine currency based on origin/user
  useEffect(() => {
    if (!isLoadingUser && !isLoadingAddresses) {
      const determinedCurrency = determineExpectedCurrency(formData, addresses, currentUser)
      setOriginDeterminedCurrency(determinedCurrency)
        }
  }, [
    formData.openToShipping,
    formData.selectedShippingAddressId,
    formData.openToPickUp,
    formData.selectedAddressId,
    formData.productType,
    addresses,
    currentUser,
    isLoadingUser,
    isLoadingAddresses,
  ])

  // Sync form currency with originDeterminedCurrency
  useEffect(() => {
    if (originDeterminedCurrency && formData.currency !== originDeterminedCurrency) {
      dispatch(updateFormData({ currency: originDeterminedCurrency }))
      const updatedRentPrices = formData.rentPriceAndTime.map(rp => ({ ...rp, currency: originDeterminedCurrency }))
      if (formData.rentPriceAndTime.length > 0 && JSON.stringify(formData.rentPriceAndTime) !== JSON.stringify(updatedRentPrices)) {
        dispatch(updateFormData({ rentPriceAndTime: updatedRentPrices }))
      }
    } else if (!originDeterminedCurrency && !formData.currency && currentUser && currentUser.country) {
      if (!formData.currency) {
        dispatch(updateFormData({ currency: 'USD' }))
      }
    }
  }, [originDeterminedCurrency, dispatch, formData.currency, formData.rentPriceAndTime, currentUser])

  // Sync local state with redux when component mounts
  useEffect(() => {
    setPriceValue(formData.price !== undefined ? String(formData.price) : '')
    setDepositValue(
      formData.rentDeposit !== undefined ? String(formData.rentDeposit) : ''
    )
  }, [])

  // Sync local state with redux when Redux state changes
  useEffect(() => {
    // Only update the local state if the formData.price or formData.rentDeposit changed
    // and if the input is not currently focused
    if (
      !document.activeElement ||
      (document.activeElement.name !== 'price' &&
        document.activeElement.name !== 'rentDeposit')
    ) {
      if (formData.price !== undefined) {
        setPriceValue(formData.price === 0 ? '' : String(formData.price))
      }
      if (formData.rentDeposit !== undefined) {
        setDepositValue(
          formData.rentDeposit === 0 ? '' : String(formData.rentDeposit)
        )
      }
    }
  }, [formData.price, formData.rentDeposit])

  // When it's a virtual fashion product or digital/online product, force forSell to true
  useEffect(() => {
    if (isVirtualFashion && !formData.forSell) {
      dispatch(
        updateFormData({ forSell: true, forRent: false, openToSwap: false })
      )
    }
    
    // For local services, set reasonable defaults: hourly rate by default
    if (isLocalService && (!formData.rentPriceAndTime || formData.rentPriceAndTime.length === 0)) {
      dispatch(
        updateFormData({ 
          forRent: true, 
          forSell: false, 
          openToSwap: false,
          rentPriceAndTime: [
            {
              number: 1,
              option: 'hour',
              price: '',
              currency: originDeterminedCurrency || defaultCurrency || 'USD'
            }
          ]
        })
      )
    }
  }, [isVirtualFashion, isLocalService, formData.forSell, formData.rentPriceAndTime, dispatch, originDeterminedCurrency, defaultCurrency])

  const handlePriceChange = e => {
    const value = e.target.value

    // Update local state with the string value
    setPriceValue(value)

    // Update redux with a number value or empty string
    const numValue = value === '' ? '' : Number(value)
    dispatch(
      updateFormData({
        price: numValue
      })
    )

    console.log('Dispatched price update:', numValue)
  }

  // No-op function since currency change is disabled - currency comes from user's country
  const handleCurrencyChange = (newCurrency) => {
    // This is intentionally a no-op since currency selection is disabled
    console.log('Currency change attempted but disabled. Currency is fixed to:', originDeterminedCurrency || formData.currency || defaultCurrency || 'USD')
    console.warn('Currency cannot be changed - it is set based on your product\'s shipping origin country.')
  }

  const handleDepositChange = e => {
    const value = e.target.value

    // Update local state with the string value
    setDepositValue(value)

    // Update redux with a number value or empty string
    const numValue = value === '' ? '' : Number(value)
    dispatch(
      updateFormData({
        rentDeposit: numValue
      })
    )

    console.log('Dispatched deposit update:', numValue)
  }

  const handleToggle = field => {
    // Create an object to update the Redux state
    const updateData = {
      [field]: !formData[field]
    }

    // Special handling for forSell toggle
    if (field === 'forSell') {
      // If turning off forSell, set price to 0
      if (formData[field]) {
        updateData.price = 0
        setPriceValue('0')
      }
      
      // For local services, can't enable forSell
      if (isLocalService) {
        return
      }
    }

    // Special handling for forRent toggle
    if (field === 'forRent') {
      // If turning off forRent, clear rental info
      if (formData[field]) {
        updateData.rentPriceAndTime = []
        updateData.requireDeposit = false
        updateData.rentDeposit = 0
        setDepositValue('0')
      } else if (
        !formData[field] &&
        (!formData.rentPriceAndTime || formData.rentPriceAndTime.length === 0)
      ) {
        // If turning on forRent and there are no rental periods, add one default
        updateData.rentPriceAndTime = [
          {
            number: '',
            option: 'day',
            price: '',
            currency: originDeterminedCurrency || defaultCurrency || 'USD'
          }
        ]
      }
      
      // For local services, forRent is always true
      if (isLocalService && !updateData[field]) {
        return
      }
    }
    
    // For local services, can't enable swap
    if (field === 'openToSwap' && isLocalService) {
      return
    }

    dispatch(updateFormData(updateData))

    console.log(`Toggled ${field} to:`, !formData[field])
  }

  const handleRentPriceChange = (index, field, value) => {
    const updatedRentPrices = [...(formData.rentPriceAndTime || [])]

    // Handle numeric inputs consistently
    const processedValue =
      field === 'price' || field === 'number'
        ? value === ''
          ? ''
          : Number(value)
        : value

    updatedRentPrices[index] = {
      ...updatedRentPrices[index],
      [field]: processedValue
    }

    dispatch(updateFormData({ rentPriceAndTime: updatedRentPrices }))

    console.log(
      `Updating rentPriceAndTime[${index}].${field} with value:`,
      processedValue
    )
  }

  // No-op function since rent currency change is disabled - currency comes from user's country
  const handleRentCurrencyChange = () => {
    // This is intentionally a no-op since currency selection is disabled
    console.log('Rent currency change attempted but disabled. Currency is fixed to:', originDeterminedCurrency || formData.currency || defaultCurrency || 'USD')
    console.warn('Currency cannot be changed - it is set based on your product\'s shipping origin country.')
  }

  const addRentPrice = () => {
    const rentPrices = [
      ...(formData.rentPriceAndTime || []),
      {
        number: '',
        option: 'day',
        price: '',
        currency: originDeterminedCurrency || defaultCurrency || 'USD'
      }
    ]
    dispatch(updateFormData({ rentPriceAndTime: rentPrices }))
  }

  const removeRentPrice = index => {
    const rentPrices = [...(formData.rentPriceAndTime || [])].filter(
      (_, i) => i !== index
    )
    dispatch(updateFormData({ rentPriceAndTime: rentPrices }))
  }

  const handleRentDurationChange = (index, value) => {
    handleRentPriceChange(index, 'number', value)
  }

  const handleRentPeriodChange = (index, period) => {
    handleRentPriceChange(index, 'option', period)
  }

  // For virtual fashion, show just the price input
  if (isVirtualFashion) {
    return (
      <div className='space-y-4 max-w-[1400px] mx-auto bg-white p-6 rounded-lg'>
        <h2 className='text-heading font-semibold text-gray-800 mb-4'>
          Pricing
        </h2>

        <div className='bg-gray-50 p-4 rounded-lg'>
          <h4 className='text-base font-medium text-gray-700 mb-3'>Price</h4>
          <PriceInputUI
            name='price'
            value={priceValue}
            onChange={handlePriceChange}
            onCurrencyChange={handleCurrencyChange}
            currency={originDeterminedCurrency || formData.currency || defaultCurrency || 'USD'}
            error={fieldErrors?.price}
            required={true}
            className='bg-white'
            disabled={isLocalService || !formData.forSell || !originDeterminedCurrency}
            disableCurrencyChange={originDeterminedCurrency !== null}
          />
          {fieldErrors.price && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors.price}</p>
          )}
          {!originDeterminedCurrency && formData.forSell && (
            <Message warning size='mini'>
              <Message.Header>Set Shipping/Pickup Origin</Message.Header>
              <p>Please set a shipping or pickup origin in the 'Availability' section to determine the currency.</p>
            </Message>
          )}
          {/* {originDeterminedCurrency && (
            <Message info size="tiny">
              <Message.Header>Currency Information</Message.Header>
              <p>The currency has been automatically set to {originDeterminedCurrency} based on your product's shipping/pickup origin country.</p>
            </Message>
          )} */}
          {!originDeterminedCurrency && !formData.currency && (
            <Message warning size="tiny"><p>Please select a currency, or set a shipping/pickup origin to determine it automatically.</p></Message>
          )}
        </div>
      </div>
    )
  }

  // For local services, show just the service rates section
  if (isLocalService) {
    return (
      <div className='space-y-4 max-w-[1400px] mx-auto bg-white p-6 rounded-lg'>
        <h2 className='text-heading font-semibold text-gray-800 mb-4'>
          Service Rates
        </h2>

        <div className='bg-gray-50 p-4 rounded-lg'>
          <h4 className='text-base font-medium text-gray-700 mb-3'>
            Your Service Rates
          </h4>
          
          {/* Period Rates */}
          <div className='mt-4 space-y-4'>
            {(formData.rentPriceAndTime || []).map((item, index) => (
              <div key={index} className='flex items-center space-x-3'>
                <RentDurationInputUI
                  value={item.number}
                  onChange={value => handleRentDurationChange(index, value)}
                  error={
                    fieldErrors?.rentPriceAndTime?.[index]?.number
                  }
                  className='flex-1'
                />
                <div className='flex-1'>
                  <PriceInputUI
                    value={item.price}
                    onChange={e =>
                      handleRentPriceChange(index, 'price', e.target.value)
                    }
                    currencyCode={originDeterminedCurrency || formData.currency || defaultCurrency || 'USD'}
                    onCurrencyChange={handleRentCurrencyChange}
                    error={fieldErrors?.rentPriceAndTime?.[index]?.price}
                    inputName={`rent-price-${index}`}
                    required={false}
                    label=''
                    disabled={!originDeterminedCurrency}
                    disableCurrencyChange={originDeterminedCurrency !== null}
                  />
                </div>
                {formData.rentPriceAndTime.length > 1 && (
                  <Button
                    icon
                    type='button'
                    onClick={() => removeRentPrice(index)}
                    className='text-red-500'
                  >
                    <Icon name='trash alternate outline' />
                  </Button>
                )}
              </div>
            ))}
            {(formData.rentPriceAndTime || []).length < 3 && (
              <Button
                type='button'
                onClick={addRentPrice}
                className='mt-2 bg-gray-100 text-gray-700 hover:bg-gray-200'
              >
                <Icon name='plus' /> Add another rate
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Regular view for products
  return (
    <div className='space-y-4 max-w-[1400px] mx-auto bg-white p-6 rounded-lg'>
      <h2 className='text-heading font-semibold text-gray-800 mb-4'>Pricing</h2>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {/* Options */}
        <div className=' p-4 rounded-lg col-span-1'>
          <h4 className='text-base font-medium text-gray-700 mb-3'>Options <span className='text-red-500'>*</span></h4>
          <div className='space-y-3'>
              <Form.Field>
                <Checkbox
                  label='For Sale'
                checked={formData.forSell || false}
                  onChange={() => handleToggle('forSell')}
                />
              </Form.Field>
              <Form.Field>
                <Checkbox
                  label='For Rent'
                checked={formData.forRent || false}
                  onChange={() => handleToggle('forRent')}
                />
              </Form.Field>
              {/* <Form.Field>
                <Checkbox
                  label='Open to Swap'
                checked={formData.openToSwap || false}
                  onChange={() => handleToggle('openToSwap')}
                />
              </Form.Field> */}
            </div>
        </div>

        {/* Price (For Sale Option) */}
        {formData.forSell && (
          <div className='bg-gray-50 p-4 rounded-lg col-span-2'>
            <h4 className='text-base font-medium text-gray-700 mb-3'>Sale Price <span className='text-red-500'>*</span></h4>
            <PriceInputUI
              name='price'
              value={priceValue}
              onChange={handlePriceChange}
              onCurrencyChange={handleCurrencyChange}
              currency={originDeterminedCurrency || formData.currency || defaultCurrency || 'USD'}
              error={fieldErrors?.price}
              required={true}
              className='bg-white'
              disabled={isLocalService || !formData.forSell || !originDeterminedCurrency}
              disableCurrencyChange={originDeterminedCurrency !== null}
            />
            {fieldErrors.price && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.price}</p>
            )}
            {!originDeterminedCurrency && formData.forSell && (
              <Message warning size='mini'>
                <Message.Header>Set Shipping/Pickup Origin</Message.Header>
                <p>Please set a shipping or pickup origin in the 'Availability' section to determine the currency.</p>
              </Message>
            )}
            {/* {originDeterminedCurrency && (
              <Message info size="tiny">
                <Message.Header>Currency Information</Message.Header>
                <p>The currency has been automatically set to {originDeterminedCurrency} based on your product's shipping/pickup origin country.</p>
              </Message>
            )} */}
            {!originDeterminedCurrency && !formData.currency && (
              <Message warning size="tiny"><p>Please select a currency, or set a shipping/pickup origin to determine it automatically.</p></Message>
            )}
        </div>
      )}
      </div>

      {/* Rent Pricing (For Rent Option) */}
      {formData.forRent && (
        <div className='bg-gray-50 p-4 rounded-lg'>
          <h4 className='text-base font-medium text-gray-700 mb-3'>
            Rent Pricing <span className='text-red-500'>*</span>
          </h4>

          {/* Rental Periods */}
          <div className='space-y-4'>
            <p className='text-sm text-gray-600'>
              Set different rental periods and prices:
            </p>

            {(formData?.rentPriceAndTime || []).map((rentPrice, index) => (
              <div
                key={index}
                className='bg-white p-4 rounded-lg border border-gray-200'
              >
                <div className='flex items-center justify-between'>
                  <div>
                    <h5 className='text-sm font-medium text-gray-700'>
                      Rental Option {index + 1}
                    </h5>
                    {rentPrice.price && rentPrice.number && rentPrice.option && (
                      <p className='text-xs text-teal-600 mt-1'>
                        {originDeterminedCurrency || formData.currency || defaultCurrency || 'USD'} {rentPrice.price} for {rentPrice.number}{' '}
                        {rentPrice.option}
                        {rentPrice.number > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  <Button
                    icon='trash'
                    type="button"
                    size='mini'
                    color='red'
                    basic
                    onClick={() => removeRentPrice(index)}
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-3'>
                  <div>
                    <PriceInputUI
                      // label="Price"
                      name={`rentPrice-${index}`}
                      value={rentPrice.price || ''}
                      onChange={e =>
                        handleRentPriceChange(index, 'price', e.target.value)
                      }
                      onCurrencyChange={handleRentCurrencyChange}
                      currency={originDeterminedCurrency || formData.currency || defaultCurrency || 'USD'}
                      error={fieldErrors?.rentPriceAndTime}
                      required={true}
                      disabled={!originDeterminedCurrency}
                      disableCurrencyChange={originDeterminedCurrency !== null}
                    />
                  </div>

                  <div>
                    <RentDurationInputUI
                      name={`rentDuration-${index}`}
                      value={rentPrice.number || ''}
                      onChange={e =>
                        handleRentDurationChange(index, e.target.value)
                      }
                      periodValue={rentPrice.option || 'day'}
                      onPeriodChange={period =>
                        handleRentPeriodChange(index, period)
                      }
                      error={fieldErrors?.rentPriceAndTime}
                      required={true}
                      disabled={!originDeterminedCurrency}
                      disableCurrencyChange={originDeterminedCurrency !== null}
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              icon
              labelPosition='left'
              color='teal'
              size='small'
              basic
              type="button"
              onClick={addRentPrice}
            >
              <Icon name='add' />
              Add Rental Period
            </Button>

            {fieldErrors?.rentPriceAndTime && (
              <div className='text-sm text-red-600'>
                {fieldErrors.rentPriceAndTime}
              </div>
            )}
          </div>

          {/* Security Deposit */}
          <div className='mt-6'>
            <Form.Field>
              <Checkbox
                label='Require Security Deposit'
                checked={formData?.requireDeposit || false}
                onChange={() => handleToggle('requireDeposit')}
              />
            </Form.Field>

            {formData?.requireDeposit && (
              <div className='mt-4 ml-4'>
                <PriceInputUI
                  label='Deposit Amount'
                  name='rentDeposit'
                  value={depositValue}
                  onChange={handleDepositChange}
                  onCurrencyChange={handleRentCurrencyChange}
                  currency={originDeterminedCurrency || formData.currency || defaultCurrency || 'USD'}
                  error={fieldErrors?.rentDeposit}
                  required={true}
                  compact={true}
                  className='bg-white'
                  disabled={!formData.requireDeposit || !originDeterminedCurrency}
                  disableCurrencyChange={originDeterminedCurrency !== null}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PricingSection
